import { useCallback, useEffect, useState } from "react";
import {
  BarChart2, Edit2, Inbox, MessageCircle, Plus, RefreshCw, Save, Send, Sparkles, Trash2, X,
} from "lucide-react";
import { useTheme } from "next-themes";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { AdminSocialSubNav } from "@/components/admin/AdminSocialSubNav";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useAdminSession } from "@/hooks/use-admin-session";

const API = "/api/social/instagram";

// ── Types ───────────────────────────────────────────────────────────────────

interface Summary {
  total_published: number;
  total_failed: number;
  posts_this_week: number;
  total_dms_sent: number;
  total_dms_received: number;
  unread_dms: number;
}

interface PostsByType {
  [key: string]: number;
}

interface DailyStat {
  date: string;
  posts_published: number;
  comments_received: number;
  dms_sent: number;
  dms_received: number;
  reply_count: number;
}

interface DmMessage {
  id: string;
  sender_id: string;
  sender_username: string | null;
  message: string;
  direction: "incoming" | "outgoing";
  is_read: boolean;
  created_at: string;
}

interface ReplyTemplate {
  id: string;
  name: string;
  type: string;
  comment_reply: string | null;
  dm_content: string | null;
  is_default: boolean;
  created_at: string;
}

// ── Dashboard Tab ────────────────────────────────────────────────────────────

const POST_TYPE_LABELS: Record<string, string> = {
  gunun_enerjisi: "Günün Enerjisi",
  numeroloji_verisi: "Numeroloji Verisi",
  kullanici_geri_bildirimi: "İlişki Uyumu",
  motivasyon: "Motivasyon",
  bilgi: "Bilgi",
};

function DashboardTab() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [postsByType, setPostsByType] = useState<PostsByType>({});
  const [dailyStats, setDailyStats] = useState<DailyStat[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/stats`);
      const json = await r.json();
      if (json.ok) {
        setSummary(json.summary ?? null);
        setPostsByType(json.posts_by_type ?? {});
        setDailyStats(json.daily_stats ?? []);
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const cards = [
    { label: "Toplam Yayın", value: summary?.total_published ?? 0, icon: <BarChart2 size={20} />, highlight: true },
    { label: "Bu Hafta", value: summary?.posts_this_week ?? 0, icon: <RefreshCw size={20} />, highlight: false },
    { label: "Hatalı Post", value: summary?.total_failed ?? 0, icon: <MessageCircle size={20} />, highlight: false },
    { label: "Gelen DM", value: summary?.total_dms_received ?? 0, icon: <Inbox size={20} />, highlight: false },
    { label: "Gönderilen DM", value: summary?.total_dms_sent ?? 0, icon: <Send size={20} />, highlight: false },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Instagram İstatistikleri</h2>
        <Button size="sm" variant="outline" onClick={fetchStats}>
          <RefreshCw size={14} className="mr-1" /> Yenile
        </Button>
      </div>

      {loading ? (
        <div className="text-center text-slate-400 py-10">Yükleniyor…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {cards.map((c) => (
              <div
                key={c.label}
                className={`rounded-2xl border p-4 ${
                  c.highlight
                    ? "border-emerald-200 bg-emerald-50 dark:border-emerald-700/50 dark:bg-emerald-900/20"
                    : "border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900"
                }`}
              >
                <div className={`mb-2 ${c.highlight ? "text-emerald-500" : "text-slate-400"}`}>{c.icon}</div>
                <div className={`text-2xl font-bold ${c.highlight ? "text-emerald-700 dark:text-emerald-400" : "text-slate-800 dark:text-slate-100"}`}>
                  {c.value}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">{c.label}</div>
              </div>
            ))}
          </div>

          {Object.keys(postsByType).length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Türe Göre Postlar</h3>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {Object.entries(postsByType).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between rounded-lg bg-slate-50 dark:bg-slate-800 px-3 py-2">
                    <span className="text-xs text-slate-600 dark:text-slate-400">
                      {POST_TYPE_LABELS[type] ?? type}
                    </span>
                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {dailyStats.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Günlük Özet</h3>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-800/50">
                    <th className="px-4 py-2 text-left text-slate-500 dark:text-slate-400 font-medium">Tarih</th>
                    <th className="px-4 py-2 text-right text-slate-500 dark:text-slate-400 font-medium">Post</th>
                    <th className="px-4 py-2 text-right text-slate-500 dark:text-slate-400 font-medium">Yorum</th>
                    <th className="px-4 py-2 text-right text-slate-500 dark:text-slate-400 font-medium">Gelen DM</th>
                    <th className="px-4 py-2 text-right text-slate-500 dark:text-slate-400 font-medium">Giden DM</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyStats.map((s) => (
                    <tr key={s.date} className="border-t border-slate-50 dark:border-slate-800/60 hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                      <td className="px-4 py-2 text-slate-700 dark:text-slate-300">{s.date}</td>
                      <td className="px-4 py-2 text-right text-slate-600 dark:text-slate-400">{s.posts_published ?? 0}</td>
                      <td className="px-4 py-2 text-right text-slate-600 dark:text-slate-400">{s.comments_received ?? 0}</td>
                      <td className="px-4 py-2 text-right text-slate-600 dark:text-slate-400">{s.dms_received ?? 0}</td>
                      <td className="px-4 py-2 text-right text-slate-600 dark:text-slate-400">{s.dms_sent ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!summary && dailyStats.length === 0 && (
            <div className="text-center py-10 text-slate-400 text-sm">Henüz istatistik yok.</div>
          )}
        </>
      )}
    </div>
  );
}

// ── DM Inbox Tab ─────────────────────────────────────────────────────────────

function DmInboxTab() {
  const { toast } = useToast();
  const [messages, setMessages] = useState<DmMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyTo, setReplyTo] = useState<DmMessage | null>(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/dm-inbox`);
      const json = await r.json();
      if (json.ok) setMessages(json.messages ?? []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  async function handleSend() {
    if (!replyTo || !replyText.trim()) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/dm-send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recipientId: replyTo.sender_id, text: replyText.trim() }),
      });
      const json = await r.json();
      if (json.ok) {
        toast({ title: "DM gönderildi" });
        setReplyTo(null);
        setReplyText("");
        fetchMessages();
      } else {
        toast({ title: "Hata", description: json.error, variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Hata", description: e.message, variant: "destructive" });
    }
    setSending(false);
  }

  const incoming = messages.filter((m) => m.direction === "incoming");
  const outgoing = messages.filter((m) => m.direction === "outgoing");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">DM Gelen Kutusu</h2>
        <Button size="sm" variant="outline" onClick={fetchMessages}>
          <RefreshCw size={14} className="mr-1" /> Yenile
        </Button>
      </div>

      {replyTo && (
        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-700 dark:bg-blue-900/20 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
              {replyTo.sender_username || replyTo.sender_id} kişisine yanıt yaz
            </span>
            <button onClick={() => { setReplyTo(null); setReplyText(""); }} className="text-slate-400 hover:text-slate-600">
              <X size={16} />
            </button>
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-400 italic">"{replyTo.message}"</div>
          <Textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="DM mesajınızı yazın…"
            rows={3}
            className="text-sm"
          />
          <Button size="sm" onClick={handleSend} disabled={sending || !replyText.trim()}>
            <Send size={14} className="mr-1" /> {sending ? "Gönderiliyor…" : "Gönder"}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="text-center text-slate-400 py-10">Yükleniyor…</div>
      ) : (
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-3 uppercase tracking-wide">
              Gelen Mesajlar ({incoming.length})
            </h3>
            {incoming.length === 0 && (
              <div className="text-sm text-slate-400 py-4 text-center">Gelen mesaj yok.</div>
            )}
            <div className="space-y-2">
              {incoming.map((m) => (
                <div key={m.id} className="flex items-start gap-3 rounded-xl border border-slate-100 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {m.sender_username || m.sender_id}
                      </span>
                      <span className="text-xs text-slate-400">{new Date(m.created_at).toLocaleString("tr-TR")}</span>
                      {!m.is_read && <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />}
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{m.message}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => { setReplyTo(m); setReplyText(""); }}>
                    <MessageCircle size={14} className="mr-1" /> Yanıtla
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-3 uppercase tracking-wide">
              Gönderilen Mesajlar ({outgoing.length})
            </h3>
            {outgoing.length === 0 && (
              <div className="text-sm text-slate-400 py-4 text-center">Gönderilen mesaj yok.</div>
            )}
            <div className="space-y-2">
              {outgoing.map((m) => (
                <div key={m.id} className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-3 dark:border-emerald-800/40 dark:bg-emerald-900/10">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                      {m.sender_username || m.sender_id}
                    </span>
                    <span className="text-xs text-slate-400">{new Date(m.created_at).toLocaleString("tr-TR")}</span>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">{m.message}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Reply Templates Tab ──────────────────────────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
  bireysel: "Bireysel",
  iliski: "İlişki",
  genel: "Genel",
};

function TemplatesTab() {
  const { toast } = useToast();
  const [templates, setTemplates] = useState<ReplyTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const blankForm = { name: "", type: "bireysel", comment_reply: "", dm_content: "", is_default: false };
  const [form, setForm] = useState({ ...blankForm });

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/reply-templates`);
      const json = await r.json();
      if (json.ok) setTemplates(json.templates ?? []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  function startCreate() {
    setForm({ ...blankForm });
    setCreating(true);
    setEditingId(null);
  }

  function startEdit(t: ReplyTemplate) {
    setForm({
      name: t.name,
      type: t.type,
      comment_reply: t.comment_reply ?? "",
      dm_content: t.dm_content ?? "",
      is_default: t.is_default,
    });
    setEditingId(t.id);
    setCreating(false);
  }

  function cancelForm() {
    setEditingId(null);
    setCreating(false);
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast({ title: "Şablon adı zorunlu.", variant: "destructive" });
      return;
    }
    const body = {
      name: form.name.trim(),
      type: form.type,
      comment_reply: form.comment_reply.trim() || null,
      dm_content: form.dm_content.trim() || null,
      is_default: form.is_default,
    };
    try {
      const url = editingId ? `${API}/reply-templates/${editingId}` : `${API}/reply-templates`;
      const method = editingId ? "PATCH" : "POST";
      const r = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const json = await r.json();
      if (json.ok) {
        toast({ title: editingId ? "Şablon güncellendi" : "Şablon oluşturuldu" });
        cancelForm();
        fetchTemplates();
      } else {
        toast({ title: "Hata", description: json.error, variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Hata", description: e.message, variant: "destructive" });
    }
  }

  async function deleteTemplate(id: string) {
    if (!confirm("Bu şablonu silmek istediğinizden emin misiniz?")) return;
    try {
      const r = await fetch(`${API}/reply-templates/${id}`, { method: "DELETE" });
      const json = await r.json();
      if (json.ok) {
        toast({ title: "Şablon silindi" });
        fetchTemplates();
      } else {
        toast({ title: "Hata", description: json.error, variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Hata", description: e.message, variant: "destructive" });
    }
  }

  const inputCls = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-600";

  const FormPanel = () => (
    <div className="rounded-xl border border-blue-200 bg-blue-50/50 dark:border-blue-800/40 dark:bg-blue-900/10 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800 dark:text-slate-100">
          {editingId ? "Şablonu Düzenle" : "Yeni Şablon"}
        </h3>
        <button onClick={cancelForm} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
          <X size={16} />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Şablon Adı *</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Örn: Bireysel Analiz"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Tip</label>
          <select
            value={form.type}
            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
            className={inputCls}
          >
            <option value="bireysel">bireysel</option>
            <option value="iliski">iliski</option>
            <option value="genel">genel</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
          💬 Yorum Yanıtı
          <span className="ml-1 font-normal text-slate-400">{"{username}"} {"{label}"} {"{num}"} kullanabilirsin</span>
        </label>
        <Textarea
          value={form.comment_reply}
          onChange={(e) => setForm((f) => ({ ...f, comment_reply: e.target.value }))}
          placeholder="@{username} {label} {num} analizin DM olarak iletildi 💌"
          rows={3}
          className="text-sm"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
          📩 DM İçeriği
          <span className="ml-1 font-normal text-slate-400">{"{label}"} {"{num}"} {"{content}"} {"{a}"} {"{b}"} kullanabilirsin</span>
        </label>
        <Textarea
          value={form.dm_content}
          onChange={(e) => setForm((f) => ({ ...f, dm_content: e.target.value }))}
          placeholder={"🔢 {label}: {num}\n\n{content}\n\n🔮 soulcircles.app"}
          rows={8}
          className="text-sm font-mono"
        />
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
          ⚠️ <code>{"{content}"}</code> o kişinin numeroloji analizini otomatik getirir — değiştirme
        </p>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="tpl_is_default"
          checked={form.is_default}
          onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))}
          className="rounded"
        />
        <label htmlFor="tpl_is_default" className="text-sm text-slate-600 dark:text-slate-400">
          Varsayılan (bu tip için öncelikli kullanılır)
        </label>
      </div>

      <div className="flex gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={cancelForm}>İptal</Button>
        <Button size="sm" onClick={handleSave}>
          <Save size={14} className="mr-1" /> Kaydet
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Şablon Yönetimi</h2>
        {!creating && !editingId && (
          <Button size="sm" onClick={startCreate}>
            <Plus size={14} className="mr-1" /> Yeni Şablon
          </Button>
        )}
      </div>

      {(creating || editingId) && <FormPanel />}

      {loading ? (
        <div className="text-center text-slate-400 py-10">Yükleniyor…</div>
      ) : templates.length === 0 ? (
        <div className="text-center text-slate-400 py-10 text-sm">Henüz şablon yok.</div>
      ) : (
        <div className="space-y-3">
          {templates.map((t) => (
            <div key={t.id} className="rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900 p-4 space-y-3">
              {/* Başlık */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-800 dark:text-slate-100">{t.name}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                    {TYPE_LABELS[t.type] ?? t.type}
                  </span>
                  {t.is_default && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                      Varsayılan
                    </span>
                  )}
                </div>
                <div className="flex gap-3 shrink-0">
                  <button
                    onClick={() => startEdit(t)}
                    className="text-xs text-blue-500 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                  >
                    Düzenle
                  </button>
                  <button
                    onClick={() => deleteTemplate(t.id)}
                    className="text-xs text-red-400 hover:text-red-600 dark:hover:text-red-300 font-medium"
                  >
                    Sil
                  </button>
                </div>
              </div>

              {/* Yorum Yanıtı */}
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">💬 YORUM YANITI</p>
                <p className="text-sm text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 rounded-lg p-2">
                  {t.comment_reply || <span className="text-slate-400 italic">Yorum yanıtı yok</span>}
                </p>
              </div>

              {/* DM İçeriği */}
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">📩 DM İÇERİĞİ</p>
                <pre className="text-sm text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 rounded-lg p-2 whitespace-pre-wrap font-sans">
                  {t.dm_content || <span className="text-slate-400 italic">DM içeriği yok</span>}
                </pre>
                <p className="text-xs text-slate-400 mt-1">
                  💡 {"{content}"} → kişinin sayısına göre numeroloji analizi otomatik gelir
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Quick Content Generator Tab ──────────────────────────────────────────────

function ContentGeneratorTab() {
  const { toast } = useToast();
  const [topic, setTopic] = useState("");
  const [postType, setPostType] = useState("gunun_enerjisi");
  const [generating, setGenerating] = useState(false);
  const [caption, setCaption] = useState("");
  const [imagePrompt, setImagePrompt] = useState("");
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [publishing, setPublishing] = useState(false);

  async function handleGenerate() {
    if (!topic.trim()) {
      toast({ title: "Konu gerekli", variant: "destructive" }); return;
    }
    setGenerating(true);
    setCaption("");
    setImagePrompt("");
    setGeneratedImage(null);
    try {
      const r = await fetch(`${API}/luna-generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ topic: topic.trim(), postType }),
      });
      const json = await r.json();
      if (json.ok) {
        const text: string = json.text ?? "";
        const imgMatch = text.match(/IMAGE_PROMPT:\s*([\s\S]+?)$/i);
        if (imgMatch) setImagePrompt(imgMatch[1].trim());
        setCaption(text.replace(/\n?IMAGE_PROMPT:[\s\S]*$/i, "").trim());
      } else {
        toast({ title: "Luna hatası", description: json.error, variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Hata", description: e.message, variant: "destructive" });
    }
    setGenerating(false);
  }

  async function handleGenerateImage() {
    if (!imagePrompt.trim()) return;
    setGeneratingImage(true);
    try {
      const r = await fetch(`${API}/generate-image`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: imagePrompt.trim() }),
      });
      const json = await r.json();
      if (json.ok && json.image) {
        setGeneratedImage(json.image);
      } else {
        toast({ title: "Görsel üretilemedi", description: json.error, variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Hata", description: e.message, variant: "destructive" });
    }
    setGeneratingImage(false);
  }

  async function handlePublish() {
    if (!caption.trim()) {
      toast({ title: "Altyazı gerekli", variant: "destructive" }); return;
    }
    setPublishing(true);
    try {
      // 1. Görseli storage'a yükle (varsa)
      let imageUrl: string | null = null;
      if (generatedImage) {
        const base64Data = generatedImage.replace(/^data:image\/\w+;base64,/, "");
        const byteChars = atob(base64Data);
        const byteArr = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
        const imgRes = await fetch(`${API}/upload-image`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ image: generatedImage }),
        });
        const imgJson = await imgRes.json();
        if (imgJson.ok) imageUrl = imgJson.url;
      }

      // 2. Post kaydet
      const saveRes = await fetch(`${API}/posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caption: caption.trim(), image_url: imageUrl, post_type: postType, status: "draft" }),
      });
      const saveJson = await saveRes.json();
      if (!saveJson.ok) {
        toast({ title: "Post kaydedilemedi", description: saveJson.error, variant: "destructive" }); return;
      }

      // 3. Yayınla
      const postId = saveJson.post?.id;
      if (postId && imageUrl) {
        const pubRes = await fetch(`${API}/posts/${postId}/publish`, { method: "POST" });
        const pubJson = await pubRes.json();
        if (pubJson.ok) {
          toast({ title: "Post yayınlandı!" });
          setCaption("");
          setImagePrompt("");
          setGeneratedImage(null);
          setTopic("");
        } else {
          toast({ title: "Yayın hatası", description: pubJson.error, variant: "destructive" });
        }
      } else {
        toast({ title: "Taslak olarak kaydedildi (görsel gerekli)" });
      }
    } catch (e: any) {
      toast({ title: "Hata", description: e.message, variant: "destructive" });
    }
    setPublishing(false);
  }

  return (
    <div className="space-y-5 max-w-2xl">
      <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Hızlı İçerik Üretici</h2>

      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Post Türü</label>
          <select
            value={postType}
            onChange={(e) => setPostType(e.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          >
            <option value="gunun_enerjisi">Günün Enerjisi</option>
            <option value="numeroloji_verisi">Numeroloji Verisi</option>
            <option value="kullanici_geri_bildirimi">İlişki Uyumu</option>
            <option value="motivasyon">Motivasyon</option>
            <option value="bilgi">Bilgi Paylaşımı</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Konu</label>
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Örn: Yaşam Yolu 7 sayısının özellikleri"
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
        </div>
        <Button onClick={handleGenerate} disabled={generating || !topic.trim()}>
          <Sparkles size={14} className="mr-1" /> {generating ? "Üretiliyor…" : "Luna ile Üret"}
        </Button>
      </div>

      {caption && (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Altyazı</label>
            <Textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={6}
              className="text-sm"
            />
          </div>

          {imagePrompt && (
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Görsel Prompt</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={imagePrompt}
                  onChange={(e) => setImagePrompt(e.target.value)}
                  className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                />
                <Button size="sm" variant="outline" onClick={handleGenerateImage} disabled={generatingImage}>
                  {generatingImage ? "Üretiliyor…" : "Görsel Üret"}
                </Button>
              </div>
            </div>
          )}

          {generatedImage && (
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Oluşturulan Görsel</label>
              <img src={generatedImage} alt="Generated" className="rounded-xl max-w-full max-h-64 object-cover border border-slate-200 dark:border-slate-700" />
            </div>
          )}

          <Button onClick={handlePublish} disabled={publishing || !caption.trim()}>
            <Send size={14} className="mr-1" /> {publishing ? "Yayınlanıyor…" : "Yayınla"}
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

type Tab = "dashboard" | "dm-inbox" | "templates" | "content";

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: "dashboard", label: "İstatistikler", icon: <BarChart2 size={15} /> },
  { id: "dm-inbox", label: "DM Gelen Kutusu", icon: <Inbox size={15} /> },
  { id: "templates", label: "Şablon Yönetimi", icon: <Edit2 size={15} /> },
  { id: "content", label: "İçerik Üretici", icon: <Sparkles size={15} /> },
];

export default function AdminSocialMediaManager() {
  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const authenticated = !!sessionQuery.data?.authenticated;
  const configured = Boolean(sessionQuery.data?.configured);
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";
  const [activeTab, setActiveTab] = useState<Tab>("dashboard");

  if (!authenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <AdminLoginCard
          configured={configured}
          onLogin={(args) => loginMutation.mutate(args)}
          isLoading={loginMutation.isPending}
          error={loginMutation.error?.message}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-950">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />
      <div className="flex flex-1 flex-col min-w-0">
        {/* Top bar */}
        <div className="sticky top-0 z-10 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/80 px-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/80">
          <NumerolojiAdminMobileSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Sosyal Medya Yöneticisi</span>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setTheme(isDark ? "light" : "dark")}
              className="rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 hover:text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
            >
              {isDark ? "☀️" : "🌙"}
            </button>
            <Button size="sm" variant="outline" onClick={() => logoutMutation.mutate()}>Çıkış</Button>
          </div>
        </div>

        <main className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8">
          <AdminSocialSubNav />

          {/* Inner tab bar */}
          <div className="mb-6 flex flex-wrap gap-1 rounded-2xl border border-slate-200 bg-white p-1 dark:border-slate-800 dark:bg-slate-900/40">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 rounded-xl px-4 py-1.5 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? "bg-slate-900 text-white dark:bg-slate-50 dark:text-slate-900"
                    : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                }`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {activeTab === "dashboard" && <DashboardTab />}
          {activeTab === "dm-inbox" && <DmInboxTab />}
          {activeTab === "templates" && <TemplatesTab />}
          {activeTab === "content" && <ContentGeneratorTab />}
        </main>
      </div>
    </div>
  );
}
